// Every signed-in user's own profile view — name, email/verification status, password change,
// sign out. auth::handlers::update_me (PATCH /auth/me) and ::change_password
// (POST /auth/change-password), neither in §4 (M1 shipped no profile-editing surface at all).
import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type {
  AccessTokenCreated,
  AccessTokenOut,
  AuthResponse,
  ListResponse,
  LlmProvidersList,
  LlmTestOut,
  MfaEnableResp,
  MfaSetupResp,
  OrgExportOut,
  OrgOut,
  SessionOut,
  TranscriptionProviderKind,
  TranscriptionProviderOut,
  TranscriptionSettingsOut,
  TranscriptionTestOut,
} from "../api/types";
import { useSession } from "../session";
import { formatDevice } from "../lib/browser";
import { isDesktop, isMobileShell, peekDesktopInfo } from "../desktop";
import { isNarrowViewport, useIsMobileLayout } from "../lib/mobileLayout";
import { getOfferSetting, setOfferSetting } from "../recorder/meetingCapture";
import { Badge, Button, Card, Dialog, Icon, Input, Select, Tabs, ToggleRow, Wordmark } from "@grackle/ui";
import { FormNotice, LoadingButton } from "../components/form";
import { OAuthButtons } from "../components/OAuthButtons";
import ConfirmDialog from "../components/ConfirmDialog";
import { TokenAccessPicker, accessToScopes, scopesLabel, type TokenAccess } from "../components/TokenScopes";
import LlmProviderPicker, { type LlmConnectBody, type LlmUpdateBody } from "../components/LlmProviderPicker";
import NotificationPrefs from "../notifications/NotificationPrefs";
import { CalendarConnectNotice, CalendarSettingsSection } from "../calendar/CalendarSettings";
import HotkeySettings from "./HotkeySettings";
import { AppShell } from "../components/AppShell";
import { lastActiveOrg } from "../lib/lastOrg";
import "../notifications/notifications.css";
import "../admin/admin.css";
import "./account.css";

// F4 / FR-BY15: this account page only offers a *speech-to-text* personal account — the
// slot a member would plug in a Whisper-compatible key for. Diarization personal accounts
// are supported server-side but have no UI yet; LLM personal adapters arrive with F1.
const PERSONAL_SLOT = "stt";

const PERSONAL_KIND_LABEL: Record<TranscriptionProviderKind, string> = {
  openai_whisper_compatible: "OpenAI Whisper-compatible",
  groq_whisper: "Groq Whisper",
  openai_audio: "OpenAI audio",
  orchard: "Orchardrun",
};

const PERSONAL_KIND_DEFAULTS: Record<TranscriptionProviderKind, { base: string; model: string }> = {
  openai_whisper_compatible: { base: "https://api.openai.com/v1", model: "whisper-1" },
  groq_whisper: { base: "https://api.groq.com/openai/v1", model: "whisper-large-v3-turbo" },
  openai_audio: { base: "https://api.openai.com/v1", model: "gpt-4o-mini-transcribe" },
  orchard: { base: "https://api.orchardrun.com/v1", model: "orchard" },
};
const PERSONAL_KINDS = Object.keys(PERSONAL_KIND_LABEL) as TranscriptionProviderKind[];

/** Status is never colour alone (CLAUDE.md): an icon and a word. */
function StatusBadge({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) {
  return (
    <Badge size="sm" tone={on ? "success" : "neutral"} icon={on ? "check-circle" : "minus"}>
      {on ? onLabel : offLabel}
    </Badge>
  );
}

/** True inside My account (MyAccountDialog), whose cards use a compact layout: one-line rows,
 * a folded password form, Save only once something changed. The phone layout's Profile and
 * Security tabs render the same cards in their full layout. */
const CompactAccount = createContext(false);

function ProfileSection() {
  const { user, refresh } = useSession();
  const [name, setName] = useState(user?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    setName(user?.name ?? "");
  }, [user?.name]);

  if (!user) return null;

  async function onSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api<AuthResponse>("PATCH", "/auth/me", { name: name.trim() });
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  }

  async function onResend() {
    setResending(true);
    try {
      await api("POST", "/auth/resend-verification");
      setResent(true);
    } catch {
      // best-effort, matches the endpoint's own always-204 contract
    } finally {
      setResending(false);
    }
  }

  const compact = useContext(CompactAccount);
  const dirty = name !== user.name;

  return (
    <div>
      <div className="account-section-title">Profile</div>

      <div className={compact ? "account-pair" : "account-stack"}>
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />

      <div className="gk-field">
        <span className="gk-field__label">Email</span>
        <div className="account-row">
          <span className="mono" style={{ fontSize: 13 }}>{user.email}</span>
          <StatusBadge on={user.email_verified} onLabel="Verified" offLabel="Unverified" />
        </div>
        {!user.email_verified ? (
          <div style={{ marginTop: 8 }}>
            {resent ? (
              <span className="gk-sm" role="status">Sent — check your inbox.</span>
            ) : (
              <Button variant="secondary" size="sm" onClick={onResend} disabled={resending}>
                {resending ? "Sending…" : "Resend verification email"}
              </Button>
            )}
          </div>
        ) : null}
      </div>
      </div>

      {error ? <FormNotice tone="error">{error}</FormNotice> : null}
      {compact && !dirty && !saved ? null : (
      <div className="admin-save-row">
        <LoadingButton onClick={onSave} loading={saving} disabled={!name.trim() || name.trim() === user.name}>
          Save changes
        </LoadingButton>
        {name !== user.name ? (
          <Button variant="ghost" onClick={() => setName(user.name)} disabled={saving}>
            Cancel
          </Button>
        ) : null}
        {saved ? <span className="admin-save-row__status">Saved.</span> : null}
      </div>
      )}
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Compact: the form folds behind a "Change password" button and folds back after a change.
  const compact = useContext(CompactAccount);
  const [open, setOpen] = useState(!compact);

  function fold() {
    setOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
  }

  // Clear success notice when fields are modified
  useEffect(() => {
    if (changed && (currentPassword || newPassword || confirmPassword)) {
      setChanged(false);
    }
  }, [changed, currentPassword, newPassword, confirmPassword]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setChanged(false);
    // §2.10 (server/src/auth/password.rs): 10-128 characters. Checked here too, so the field
    // needs no standing hint and the message reads better than the server's.
    if (newPassword.length < 10 || newPassword.length > 128) {
      setError("Use 10 to 128 characters for the new password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api("POST", "/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChanged(true);
      if (compact) setOpen(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setChanged(false), 4000);
    } catch (err) {
      if (err instanceof ApiFail) {
        setError(err.code === "invalid_credentials" ? "Current password is incorrect." : err.message);
      } else {
        setError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <div className="account-line">
        <div className="account-line__body">
          <div className="account-section-title">Password</div>
          <div className="account-section-sub">
            {changed ? "Password changed." : "Changing it signs out every other device — this one stays signed in."}
          </div>
        </div>
        <Button variant="secondary" onClick={() => { setChanged(false); setOpen(true); }}>
          Change password
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="account-section-title">Password</div>
      <div className="account-section-sub">Changing it signs out every other device — this one stays signed in.</div>
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        {error ? <FormNotice tone="error">{error}</FormNotice> : null}
        {changed ? <FormNotice tone="success">Password changed.</FormNotice> : null}
        <div className="admin-save-row">
          <LoadingButton type="submit" loading={submitting} disabled={!currentPassword || !newPassword || !confirmPassword}>
            Change password
          </LoadingButton>
          {compact ? (
            <Button variant="ghost" onClick={fold} disabled={submitting}>
              Cancel
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

// FR-A6: TOTP setup/enable/disable. Three phases: at rest (show status + a way to turn it on
// or off), "setting up" (secret + code entry, POST /auth/mfa/enable), and "codes" (the 10
// recovery codes, shown exactly once per enable() — never re-fetchable afterward).
function MfaSection() {
  const compact = useContext(CompactAccount);
  const { user, refresh } = useSession();
  const [phase, setPhase] = useState<"idle" | "setup" | "codes" | "disable">("idle");
  const [setupData, setSetupData] = useState<MfaSetupResp | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function onStartSetup() {
    setError(null);
    setBusy(true);
    try {
      const res = await api<MfaSetupResp>("POST", "/auth/mfa/setup");
      setSetupData(res);
      setCode("");
      setPhase("setup");
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't start setup.");
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmSetup(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<MfaEnableResp>("POST", "/auth/mfa/enable", { code: code.trim() });
      setRecoveryCodes(res.recovery_codes);
      setPhase("codes");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiFail ? (err.code === "validation" ? "That code doesn't match." : err.message) : "Couldn't enable two-factor authentication.");
    } finally {
      setBusy(false);
    }
  }

  async function onDisable(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api("POST", "/auth/mfa/disable", { password: disablePassword });
      setDisablePassword("");
      setPhase("idle");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiFail ? (err.code === "invalid_credentials" ? "Incorrect password." : err.message) : "Couldn't turn off two-factor authentication.");
    } finally {
      setBusy(false);
    }
  }

  function onDoneWithCodes() {
    setPhase("idle");
    setSetupData(null);
    setRecoveryCodes([]);
  }

  return (
    <div>
      {phase === "idle" && compact ? null : <div className="account-section-title">Two-factor authentication</div>}

      {phase === "idle" && compact ? (
        <>
          <div className="account-line">
            <div className="account-line__body account-line__body--row">
              <div className="account-section-title">Two-factor authentication</div>
              <StatusBadge on={user.mfa_enabled} onLabel="On" offLabel="Off" />
            </div>
            {user.mfa_enabled ? (
              <LoadingButton variant="secondary" onClick={() => { setPhase("disable"); setError(null); }}>
                Turn off
              </LoadingButton>
            ) : (
              <LoadingButton onClick={onStartSetup} loading={busy}>
                Set up
              </LoadingButton>
            )}
          </div>
          {user.mfa_enabled ? (
            <div className="account-mfa-help">Turning it off means anyone with your password can access your account.</div>
          ) : null}
          {error ? <FormNotice tone="error">{error}</FormNotice> : null}
        </>
      ) : phase === "idle" ? (
        <div className="account-mfa-status">
          <div className="account-row">
            <StatusBadge on={user.mfa_enabled} onLabel="On" offLabel="Off" />
          </div>
          {error ? <FormNotice tone="error">{error}</FormNotice> : null}
          <div style={{ marginTop: 10 }}>
            {user.mfa_enabled ? (
              <>
                <LoadingButton variant="secondary" onClick={() => { setPhase("disable"); setError(null); }}>
                  Turn off two-factor
                </LoadingButton>
                <div className="account-mfa-help">
                  Turning off MFA means anyone with your password can access your account.
                </div>
              </>
            ) : (
              <LoadingButton onClick={onStartSetup} loading={busy}>
                Set up two-factor authentication
              </LoadingButton>
            )}
          </div>
        </div>
      ) : null}

      {phase === "setup" && setupData ? (
        <form className="auth-form" onSubmit={onConfirmSetup} noValidate>
          <FormNotice>
            Add this key to your authenticator app, then enter the 6-digit code it generates.
          </FormNotice>
          <div className="mfa-secret mono">{setupData.secret}</div>
          <Input
            label="6-digit code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error ? <FormNotice tone="error">{error}</FormNotice> : null}
          <div className="auth-actions">
            <LoadingButton type="submit" loading={busy} disabled={!code.trim()}>
              Enable
            </LoadingButton>
            <LoadingButton type="button" variant="ghost" onClick={() => { setPhase("idle"); setSetupData(null); }}>
              Cancel
            </LoadingButton>
          </div>
        </form>
      ) : null}

      {phase === "codes" ? (
        <div>
          <FormNotice>
            Save these recovery codes somewhere safe — each works once, and this is the only time
            they're shown.
          </FormNotice>
          <div className="recovery-codes mono">
            {recoveryCodes.map((c) => (
              <div key={c}>{c}</div>
            ))}
          </div>
          <LoadingButton onClick={onDoneWithCodes} style={{ marginTop: 10 }}>
            I've saved these
          </LoadingButton>
        </div>
      ) : null}

      {phase === "disable" ? (
        <form className="auth-form" onSubmit={onDisable} noValidate>
          <Input
            label="Confirm your password"
            type="password"
            autoComplete="current-password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
          />
          {error ? <FormNotice tone="error">{error}</FormNotice> : null}
          <div className="auth-actions">
            <LoadingButton type="submit" loading={busy} disabled={!disablePassword}>
              Turn off
            </LoadingButton>
            <LoadingButton type="button" variant="ghost" onClick={() => { setPhase("idle"); setDisablePassword(""); }}>
              Cancel
            </LoadingButton>
          </div>
        </form>
      ) : null}
    </div>
  );
}

// FR-A5: every live session for this account, with a revoke action for anything that isn't
// the one currently viewing this page.
/** `collapsed`: the list sits behind a "N signed-in sessions" disclosure (My account, where a
 * long list would push the cards below it out of reach); the Settings tab shows it open. */
function SessionsSection({ collapsed = false }: { collapsed?: boolean }) {
  const [sessions, setSessions] = useState<SessionOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const res = await api<{ items: SessionOut[] }>("GET", "/auth/sessions");
      setSessions(res.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load sessions.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onRevoke(id: string) {
    setRevokingId(id);
    setError(null);
    try {
      await api("DELETE", `/auth/sessions/${id}`);
      setSessions((prev) => prev?.filter((s) => s.id !== id) ?? null);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't sign out that session.");
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div>
      <div className="account-section-title">Sessions</div>
      <div className="account-section-sub">Devices and browsers currently signed in to your account.</div>
      {error ? <FormNotice tone="error">{error}</FormNotice> : null}
      {!sessions ? (
        <div className="status-row">Loading…</div>
      ) : collapsed ? (
        <details className="account-disclosure">
          <summary>
            <Icon name="caret-right" size={16} />
            {sessions.length === 1 ? "1 signed-in session" : `${sessions.length} signed-in sessions`}
          </summary>
          {list(sessions)}
        </details>
      ) : (
        list(sessions)
      )}
    </div>
  );

  function list(items: SessionOut[]) {
    return (
      <div className="list">
        {items.map((s) => (
          <div className="list-row" key={s.id}>
            <div className="list-row__body">
              <div className="list-row__title">
                {s.client === "desktop" ? (s.device_name ?? "Desktop app") : formatDevice(s.user_agent)}
                {s.current ? (
                  <Badge size="sm" tone="accent">
                    This device
                  </Badge>
                ) : null}
              </div>
              <div className="list-row__sub mono">
                {s.created_ip ?? "unknown IP"} · last used {new Date(s.last_used_at).toLocaleString()}
              </div>
            </div>
            {!s.current ? (
              <Button
                variant="secondary"
                size="sm"
                style={{ flexShrink: 0 }}
                onClick={() => onRevoke(s.id)}
                disabled={revokingId === s.id}
              >
                {revokingId === s.id ? "Signing out…" : "Sign out"}
              </Button>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
}

// FR-API6: personal access tokens for the hosted MCP endpoint. Plaintext is shown once.
export function AccessTokensSection() {
  const [tokens, setTokens] = useState<AccessTokenOut[] | null>(null);
  const [name, setName] = useState("");
  const [access, setAccess] = useState<TokenAccess>("write");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<AccessTokenCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(() => {
    api<ListResponse<AccessTokenOut>>("GET", "/auth/tokens")
      .then((res) => setTokens(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load tokens."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api<AccessTokenCreated>("POST", "/auth/tokens", {
        name: trimmed,
        scopes: accessToScopes(access),
      });
      setCreated(res);
      setName("");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't create that token.");
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    setRevokingId(id);
    setError(null);
    try {
      await api("DELETE", `/auth/tokens/${id}`);
      if (created?.id === id) setCreated(null);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't revoke that token.");
    } finally {
      setRevokingId(null);
    }
  }

  async function onCopy(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
    } catch {
      // Clipboard may be denied; the secret is still on screen to copy by hand.
    }
  }

  return (
    <div>
      <div className="account-section-title">Access tokens</div>
      <div className="account-section-sub">
        Personal tokens for Grok Bot or Cursor. Connect at <span className="mono">/mcp</span> with
        the Bearer header, or call the REST API (<a href="/developers">API reference</a>). The
        secret is shown once. A read-only token can search and read but cannot change anything.
      </div>
      <form onSubmit={onCreate} className="account-row" style={{ marginBottom: 12, alignItems: "flex-end" }}>
        <Input label="Token name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Grok Bot" />
        <TokenAccessPicker value={access} onChange={setAccess} />
        <LoadingButton type="submit" loading={creating} disabled={!name.trim()} style={{ marginBottom: 22 }}>
          Create
        </LoadingButton>
      </form>
      {created ? (
        <div className="mfa-secret" style={{ marginBottom: 12 }}>
          <div className="account-section-sub" style={{ marginBottom: 8 }}>
            Copy this now — it will not be shown again.
          </div>
          <div className="mono" style={{ wordBreak: "break-all" }}>{created.secret}</div>
          <Button variant="secondary" size="sm" icon="copy" style={{ marginTop: 8 }} onClick={() => onCopy(created.secret)}>
            Copy
          </Button>
        </div>
      ) : null}
      {error ? <FormNotice tone="error">{error}</FormNotice> : null}
      {!tokens ? (
        <div className="status-row">Loading…</div>
      ) : tokens.length === 0 ? (
        <div className="account-section-sub">No personal tokens yet.</div>
      ) : (
        <div className="list">
          {tokens.map((t) => (
            <div className="list-row" key={t.id}>
              <div className="list-row__body">
                <div className="list-row__title">
                  {t.name}
                  <Badge size="sm">personal</Badge>
                  <Badge size="sm">{scopesLabel(t.scopes)}</Badge>
                </div>
                <div className="list-row__sub mono">
                  {t.token_prefix}… · created {new Date(t.created_at).toLocaleDateString()}
                  {t.last_used_at ? ` · last used ${new Date(t.last_used_at).toLocaleString()}` : " · never used"}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                style={{ flexShrink: 0 }}
                onClick={() => onRevoke(t.id)}
                disabled={revokingId === t.id}
              >
                {revokingId === t.id ? "Revoking…" : "Revoke"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// F1 (SD36, FR-BY15): a personal model section per org that currently allows personal
// models — hidden entirely for an org that doesn't (brief: "personal model section only when
// the org allows personal models, hidden otherwise"). `eligible` comes from
// usePersonalModelOrgs in AccountPage.
function PersonalModelSection({ eligible }: { eligible: OrgOut[] }) {
  if (eligible.length === 0) return null;

  return (
    <div>
      <div className="account-section-title">Your own language model</div>
      <div className="account-section-sub">
        Bring your own model account for meeting notes. It's used instead of the org's model only
        while the org allows it, and only for recordings you own.
      </div>
      {eligible.map((org) => (
        <PersonalModelForOrg key={org.id} orgId={org.id} orgName={org.name} />
      ))}
    </div>
  );
}

function PersonalModelForOrg({ orgId, orgName }: { orgId: string; orgName: string }) {
  const [providers, setProviders] = useState<LlmProvidersList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<LlmProvidersList>("GET", `/me/ai/providers?org_id=${orgId}&slot=llm`)
      .then((next) => {
        setProviders(next);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load your personal model."));
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError && !providers) {
    return <FormNotice tone="error">{loadError}</FormNotice>;
  }
  if (!providers) return <div className="account-section-sub">Loading…</div>;

  return (
    <div style={{ marginTop: 14 }}>
      <div className="account-section-sub" style={{ marginBottom: 8 }}>
        {orgName}
      </div>
      <LlmProviderPicker
        providers={providers.items}
        onConnect={async (body: LlmConnectBody) => {
          await api("POST", "/me/ai/providers", { ...body, org_id: orgId, slot: "llm" });
          load();
        }}
        onUpdate={async (id: string, body: LlmUpdateBody) => {
          await api("PATCH", `/me/ai/providers/${id}?org_id=${orgId}`, body);
          load();
        }}
        onDelete={async (id: string) => {
          await api("DELETE", `/me/ai/providers/${id}?org_id=${orgId}`);
          load();
        }}
        onTest={(id: string) => api<LlmTestOut>("POST", `/me/ai/providers/${id}/test?org_id=${orgId}`)}
        onActivate={async (id: string) => {
          await api("POST", `/me/ai/providers/${id}/activate?org_id=${orgId}`);
          load();
        }}
      />
    </div>
  );
}

// FR-A2: connect a Google/Microsoft identity to this account for one-click sign-in later.
function ConnectedAccountsSection() {
  const compact = useContext(CompactAccount);
  const [params] = useSearchParams();
  const linked = params.get("linked") === "1";
  return (
    <div>
      <div className="account-section-title">Connected accounts</div>
      {compact ? null : (
        <div className="account-section-sub">Sign in faster next time by linking Google or Microsoft to this account.</div>
      )}
      {linked ? <FormNotice tone="success">Account connected.</FormNotice> : null}
      <div className={compact ? "account-oauth-row" : undefined}>
        <OAuthButtons link label="Connect an account" />
      </div>
    </div>
  );
}

function exportStatusLabel(status: OrgExportOut["status"]): string {
  return status === "done" ? "Ready" : status === "failed" ? "Failed" : status === "running" ? "Building…" : "Queued";
}

/** Exported for `AccountPage.test.ts` (W7: "'Download my data' posts scope user") — FR-AD6's
 * per-user export is always `scope: "user"`, never the admin-only `"org"` default. */
export function requestUserDataExport(orgId: string): Promise<OrgExportOut> {
  return api<OrgExportOut>("POST", `/orgs/${orgId}/exports`, { scope: "user" });
}

/** Exported for `AccountPage.test.ts`: the newest `scope: "user"` export that belongs to
 * `userId` (the list is newest-first; an admin's list also carries other members' exports). */
export function latestOwnExport(items: OrgExportOut[], userId: string | undefined): OrgExportOut | null {
  if (!userId) return null;
  return items.find((e) => e.scope === "user" && e.user_id === userId) ?? null;
}

/** FR-AD6 "download my data": one `scope: "user"` export per active org, tracked by the id
 * the create call returns (not by filtering the org's export list) — an org admin's
 * `GET /exports` also returns every other member's exports for operator visibility
 * (org_export.rs), so matching on id is what keeps this section showing only the export this
 * person actually asked for here, not someone else's. */
function OrgExportRow({ org }: { org: { id: string; name: string } }) {
  const { user } = useSession();
  const [current, setCurrent] = useState<OrgExportOut | null>(null);

  // Pick this person's own latest export back up after a reload, so a ready download (or one
  // still building) isn't lost just because the page was refreshed.
  useEffect(() => {
    let cancelled = false;
    api<ListResponse<OrgExportOut>>("GET", `/orgs/${org.id}/exports`)
      .then((res) => {
        const mine = latestOwnExport(res.items, user?.id);
        if (!cancelled && mine) setCurrent((cur) => cur ?? mine);
      })
      .catch(() => {
        // Best-effort — the row still works as a fresh "Request export".
      });
    return () => {
      cancelled = true;
    };
  }, [org.id, user?.id]);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const poll = useCallback(async () => {
    if (!current || (current.status !== "queued" && current.status !== "running")) return;
    try {
      const res = await api<ListResponse<OrgExportOut>>("GET", `/orgs/${org.id}/exports`);
      const match = res.items.find((e) => e.id === current.id);
      if (match) setCurrent(match);
    } catch {
      // Best-effort — the next poll tick (or a manual refresh) will try again.
    }
  }, [org.id, current]);

  useEffect(() => {
    if (!current || (current.status !== "queued" && current.status !== "running")) return;
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [current, poll]);

  async function onRequest() {
    setRequesting(true);
    setError(null);
    try {
      const created = await requestUserDataExport(org.id);
      setCurrent(created);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't start an export.");
    } finally {
      setRequesting(false);
    }
  }

  return (
    <div className="session-row">
      <div className="session-row-body">
        <div className="session-row-title">{org.name}</div>
        {/* No export yet: the section's description already says what one holds. */}
        {current ? (
          <div className="session-row-sub">
            {`${exportStatusLabel(current.status)} · requested ${new Date(current.created_at).toLocaleString()}`}
            {current.status === "done" && current.expires_at
              ? ` · link expires ${new Date(current.expires_at).toLocaleString()}`
              : ""}
            {current.status === "failed" && current.error ? ` — ${current.error}` : ""}
          </div>
        ) : null}
        {error ? <FormNotice tone="error">{error}</FormNotice> : null}
      </div>
      {current?.status === "done" ? (
        <a
          className="gk-btn gk-btn--secondary gk-btn--sm"
          style={{ flexShrink: 0 }}
          href={`/api/v1/orgs/${org.id}/exports/${current.id}/download`}
        >
          Download
        </a>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          style={{ flexShrink: 0 }}
          onClick={() => void onRequest()}
          disabled={requesting || current?.status === "queued" || current?.status === "running"}
        >
          {current?.status === "queued" || current?.status === "running" ? "Building…" : "Request export"}
        </Button>
      )}
    </div>
  );
}

function DataExportSection() {
  const { orgs } = useSession();

  if (orgs.length === 0) return null;

  return (
    <div>
      <div className="account-section-title">Download my data</div>
      <div className="account-section-sub">
        For each organization you belong to, get an archive of your own recordings and their
        metadata.
      </div>
      <div className="session-list">
        {orgs.map((org) => (
          <OrgExportRow key={org.id} org={org} />
        ))}
      </div>
    </div>
  );
}

// FR-A4 / F14a: shown only inside the Tauri shell — the one place a packaged build's
// effective API origin is visible, since a release build silently defaults it at compile
// time (`option_env!`) and a dev override is otherwise invisible to whoever is looking at it.
function AboutSection() {
  if (!isDesktop()) return null;
  const info = peekDesktopInfo();
  if (!info) return null;
  return (
    <div>
      <div className="account-section-title">About</div>
      <div className="account-section-sub">
        Grackle desktop · {info.shell} · API: {info.api_origin || "same-origin (dev)"}
      </div>
    </div>
  );
}

// F8 / FR-RD15: per-user "Offer to record detected Zoom/Teams meetings" (default on). Stored
// client-side (this device's local settings) — there is no server-side user-preferences store.
function MeetingOfferSection() {
  const [on, setOn] = useState(() => getOfferSetting());
  if (!isDesktop() || isMobileShell()) return null;
  return (
    <div>
      <div className="account-section-title">Meetings</div>
      <ToggleRow
        label="Offer to record detected Zoom/Teams meetings"
        description="Shows a banner and a notification when a Zoom or Teams meeting window opens. Recording never starts on its own."
        checked={on}
        onChange={(v) => {
          setOn(v);
          setOfferSetting(v);
        }}
        divided
      />
    </div>
  );
}

// FR-A8: soft-delete + revoke every session. Password-confirmed, same bar as MFA disable —
// this is the single most destructive action available on this page.
function DeleteAccountSection() {
  const compact = useContext(CompactAccount);
  const { signOut } = useSession();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDelete() {
    if (!password) {
      setError("Enter your password to confirm.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api("POST", "/auth/delete-account", { password });
      await signOut();
      navigate("/signin", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiFail
          ? err.code === "invalid_credentials"
            ? "Incorrect password."
            : err.message
          : "Couldn't delete account.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className={compact ? "danger-zone account-line" : "danger-zone"}>
      <div className={compact ? "account-line__body" : "account-stack"}>
        <div className="account-section-title">Delete account</div>
        <div className="account-section-sub">
          Permanently deletes your account and signs you out everywhere. This can't be undone.
        </div>
      </div>
      <LoadingButton variant="danger" onClick={() => setDialogOpen(true)}>
        Delete account
      </LoadingButton>
      {/* FR-A8: password-confirmed — the field lives inside the dialog so the confirm
          actually carries the credential the endpoint requires. */}
      <ConfirmDialog
        open={dialogOpen}
        title="Delete account"
        message={
          <>
            Permanently delete your account, all sessions, and all your data. This can't be undone.
            <Input
              label="Confirm your password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={error ?? undefined}
            />
          </>
        }
        confirmLabel="Delete account"
        danger
        loading={submitting}
        onClose={() => { setDialogOpen(false); setPassword(""); setSubmitting(false); setError(null); }}
        onConfirm={onDelete}
      />
    </div>
  );
}

// FR-BY15: a member's own provider account for one org, used when that org allows it.
// Disconnecting is always allowed, even if the org later revokes the policy (FR-BY15).
function PersonalProviderCard({
  orgId,
  provider,
  onChanged,
}: {
  orgId: string;
  provider: TranscriptionProviderOut;
  onChanged: () => void;
}) {
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TranscriptionTestOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  async function onTest() {
    setTesting(true);
    setError(null);
    setTest(null);
    try {
      const result = await api<TranscriptionTestOut>(
        "POST",
        `/me/ai/providers/${provider.id}/test?org_id=${orgId}`,
      );
      setTest(result);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't reach that provider.");
    } finally {
      setTesting(false);
    }
  }

  async function onDisconnectConfirm() {
    setConfirmOpen(false);
    setDisconnecting(true);
    setError(null);
    try {
      await api("DELETE", `/me/ai/providers/${provider.id}?org_id=${orgId}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't disconnect that account.");
      setDisconnecting(false);
    }
  }

  return (
    <div className="account-row" style={{ alignItems: "flex-start", flexDirection: "column", gap: 8 }}>
      <div className="account-row" style={{ width: "100%", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {provider.label || PERSONAL_KIND_LABEL[provider.kind]}
          </div>
          <div className="account-section-sub" style={{ margin: 0 }}>
            <span className="mono">•••• {provider.api_key_last4}</span> · connected{" "}
            {new Date(provider.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      {test ? (
        <FormNotice tone={test.ok ? "success" : "error"}>
          {test.ok ? `Reached ${test.endpoint} with ${test.configured_model}.` : (test.error ?? "Test failed.")}
        </FormNotice>
      ) : null}
      {error ? <FormNotice tone="error">{error}</FormNotice> : null}
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="secondary" size="sm" onClick={() => void onTest()} disabled={testing}>
          {testing ? "Testing…" : "Test"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={disconnecting}
        >
          {disconnecting ? "Disconnecting…" : "Disconnect"}
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Disconnect your account"
        message="What you've already generated stays with the organization, still labelled as yours. New work goes back to the organization's provider, or off if there isn't one."
        confirmLabel="Disconnect"
        danger
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void onDisconnectConfirm()}
      />
    </div>
  );
}

// One org's personal-account card. Only rendered for orgs where `allow_personal_models`
// is already confirmed true (usePersonalModelOrgs reads that from GET /orgs/{id}/transcription).
function PersonalAiOrgSection({ org }: { org: OrgOut }) {
  const [providers, setProviders] = useState<TranscriptionProviderOut[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kind, setKind] = useState<TranscriptionProviderKind>("openai_whisper_compatible");
  const [label, setLabel] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState(PERSONAL_KIND_DEFAULTS.openai_whisper_compatible.base);
  const [modelId, setModelId] = useState(PERSONAL_KIND_DEFAULTS.openai_whisper_compatible.model);
  const [apiKey, setApiKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<{ items: TranscriptionProviderOut[] }>(
      "GET",
      `/me/ai/providers?org_id=${org.id}&slot=${PERSONAL_SLOT}`,
    )
      .then((res) => setProviders(res.items))
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load your AI account."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  function onKindChange(next: TranscriptionProviderKind) {
    setKind(next);
    const defaults = PERSONAL_KIND_DEFAULTS[next];
    setApiBaseUrl(defaults.base);
    setModelId(defaults.model);
  }

  async function onConnect() {
    setConnecting(true);
    setConnectError(null);
    try {
      await api("POST", "/me/ai/providers", {
        org_id: org.id,
        slot: PERSONAL_SLOT,
        kind,
        label: label.trim(),
        api_base_url: apiBaseUrl.trim() || undefined,
        model_id: modelId.trim() || undefined,
        api_key: apiKey.trim(),
      });
      setApiKey("");
      setLabel("");
      load();
    } catch (err) {
      setConnectError(err instanceof ApiFail ? err.message : "Couldn't connect that account.");
    } finally {
      setConnecting(false);
    }
  }

  if (loadError) {
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{org.name}</div>
        <FormNotice tone="error">{loadError}</FormNotice>
      </div>
    );
  }
  if (providers === null) return null;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{org.name}</div>
      {providers.length > 0 ? (
        <div className="list">
          {providers.map((p) => (
            <PersonalProviderCard key={p.id} orgId={org.id} provider={p} onChanged={load} />
          ))}
        </div>
      ) : (
        // Same spacing as the admin console's connect form; a bare <div> stacked the fields
        // with no gap, so the Kind select ran into the Label row.
        <div className="admin-provider-connect">
          <div className="account-section-sub">
            Processed under your own provider's terms — retention, training policy, and region
            are theirs, not {org.name}'s.
          </div>
          <Select
            id={`personal-kind-${org.id}`}
            className="admin-field--narrow"
            label="Kind"
            value={kind}
            onChange={(v) => onKindChange(v as TranscriptionProviderKind)}
            options={PERSONAL_KINDS.map((k) => ({ value: k, label: PERSONAL_KIND_LABEL[k] }))}
          />
          <div className="admin-two-col">
            <Input label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
            <Input label="Model id" value={modelId} onChange={(e) => setModelId(e.target.value)} />
          </div>
          <Input label="API base URL" value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} />
          <Input
            label="API key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            help="Stored encrypted. Never returned after save."
          />
          {connectError ? <FormNotice tone="error">{connectError}</FormNotice> : null}
          <div className="admin-actions">
            <LoadingButton onClick={() => void onConnect()} loading={connecting} disabled={!apiKey.trim()}>
              Connect
            </LoadingButton>
          </div>
        </div>
      )}
    </div>
  );
}

// FR-BY15: only shown for orgs that have turned on `allow_personal_models` (usePersonalModelOrgs).
function PersonalAiSection({ eligible }: { eligible: OrgOut[] }) {
  if (eligible.length === 0) return null;

  return (
    <div>
      <div className="account-section-title">Your own transcription account</div>
      <div className="account-section-sub">
        Connect your own speech-to-text account for an organization that allows it. Your content
        still runs through the recording product normally — only transcription spends your key.
      </div>
      {eligible.map((org) => (
        <PersonalAiOrgSection key={org.id} org={org} />
      ))}
    </div>
  );
}

/** FR-BY15: the orgs this member belongs to that currently allow personal models, or null while
 * loading — read from GET /orgs/{id}/transcription, which any member may call for exactly this
 * reason (transcription.rs get_settings doc comment). Policy can change at any time, so this
 * re-checks every org on every mount rather than trusting a cached flag. One check shared by
 * both AI models sections and the tab itself. */
function usePersonalModelOrgs(): OrgOut[] | null {
  const { orgs } = useSession();
  const [eligible, setEligible] = useState<OrgOut[] | null>(null);

  useEffect(() => {
    if (orgs.length === 0) {
      setEligible([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      orgs.map((org) =>
        api<TranscriptionSettingsOut>("GET", `/orgs/${org.id}/transcription`)
          .then((s) => (s.allow_personal_models ? org : null))
          .catch(() => null),
      ),
    ).then((results) => {
      if (!cancelled) setEligible(results.filter((o): o is OrgOut => o !== null));
    });
    return () => {
      cancelled = true;
    };
  }, [orgs]);

  return eligible;
}

/** Who you are and how you sign in, in the order most sites list them: identity, sign-in and
 * security, where you're signed in, then your data and the account itself. In the desktop-width
 * layout it opens as a dialog from the sidebar, the account menu and the Settings page's top
 * bar, and Settings keeps only how the app behaves. The phone layout has no dialog: these cards
 * stay on Settings' Profile and Security tabs there. */
export function MyAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <Dialog open={open} onClose={onClose} title="My account" width={640}>
      <CompactAccount.Provider value={true}>
      <div className="account-dialog">
        {[
          <ProfileSection key="profile" />,
          <PasswordSection key="password" />,
          <MfaSection key="mfa" />,
          <ConnectedAccountsSection key="connected" />,
          <SessionsSection key="sessions" collapsed />,
          <DataExportSection key="export" />,
          <DeleteAccountSection key="delete" />,
        ].map((section) => (
          <section className="account-section account-dialog__section" key={section.key}>
            {section}
          </section>
        ))}
      </div>
      </CompactAccount.Provider>
    </Dialog>
  );
}

type AccountTab = "profile" | "security" | "ai" | "notifications" | "calendars" | "tokens" | "device";

const ACCOUNT_TABS: { value: AccountTab; label: string }[] = [
  { value: "profile", label: "Profile" },
  { value: "security", label: "Security" },
  { value: "ai", label: "AI models" },
  { value: "notifications", label: "Notifications" },
  { value: "calendars", label: "Calendars" },
  { value: "tokens", label: "Access tokens" },
  { value: "device", label: "This device" },
];

/** The tabs whose cards are My account's in the desktop-width layout. There a link to one (or
 * the OAuth "connect" round trip, which returns with ?linked=1) opens the dialog over Settings. */
const ACCOUNT_DIALOG_TABS: string[] = ["profile", "security"];

function isAccountTab(value: string | null): value is AccountTab {
  return ACCOUNT_TABS.some((t) => t.value === value);
}

export default function AccountPage() {
  const { user, signOut, orgs = [] } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // The tab is in the URL so a link (and the OAuth "connect" round trip, which returns with
  // ?linked=1) lands on the right section.
  const mobile = useIsMobileLayout();
  const rawTab = params.get("tab");
  const linked = params.get("linked") === "1";
  const wantsAccount = ACCOUNT_DIALOG_TABS.includes(rawTab ?? "") || linked;
  const home: AccountTab = mobile ? "profile" : "notifications";
  const requested: AccountTab = isAccountTab(rawTab) ? rawTab : linked ? "security" : home;
  const [accountOpen, setAccountOpen] = useState(() => !isNarrowViewport() && !isMobileShell() && wantsAccount);
  // AI models only exists while some org allows personal models; without one it would be an
  // empty panel, so the tab is hidden and ?tab=ai falls back to Notifications. While the check is
  // in flight a ?tab=ai link keeps its tab (and an empty panel) rather than flashing another.
  const personalModelOrgs = usePersonalModelOrgs();
  const aiAvailable = personalModelOrgs === null ? requested === "ai" : personalModelOrgs.length > 0;
  // This device holds the settings that live in the desktop (or mobile) app on this machine, not
  // in the account: meeting prompts, global hotkeys, the app's version. A browser has none of
  // them, so the tab only exists in the app and ?tab=device falls back to Notifications elsewhere.
  const deviceAvailable = isDesktop();
  // FR-M9: a calendar connection belongs to an org, so with none there is nothing to connect.
  const calendarsAvailable = orgs.length > 0;
  const available = (t: AccountTab) =>
    (t !== "ai" || aiAvailable) &&
    (t !== "device" || deviceAvailable) &&
    (t !== "calendars" || calendarsAvailable) &&
    (mobile || !ACCOUNT_DIALOG_TABS.includes(t));
  const tab: AccountTab = available(requested) ? requested : home;
  const tabs = ACCOUNT_TABS.filter((t) => available(t.value));

  function closeAccount() {
    setAccountOpen(false);
    // Drop the legacy ?tab=profile|security / ?linked=1 that opened it, so a reload doesn't.
    if (ACCOUNT_DIALOG_TABS.includes(rawTab ?? "") || params.has("linked")) {
      const nextParams = new URLSearchParams(params);
      if (ACCOUNT_DIALOG_TABS.includes(rawTab ?? "")) nextParams.delete("tab");
      nextParams.delete("linked");
      setParams(nextParams, { replace: true });
    }
  }

  function onTab(next: string) {
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
  }

  async function onSignOut() {
    await signOut();
    navigate("/signin", { replace: true });
  }

  if (!user) return null;

  const content = (
    <div className="account-content">
      <h1 className="account-page-title">Settings</h1>
      <Tabs className="account-tabs" items={tabs} value={tab} onChange={onTab} />
      {/* DS SettingsScreen: one white card per settings group. */}
      <div className="account-panel" role="tabpanel">
        {tab === "device" ? (
          <p className="account-tab-note">These settings apply only to Grackle on this device.</p>
        ) : null}
        {tab === "calendars" ? <CalendarConnectNotice /> : null}
        {(tab === "profile"
          ? [<ProfileSection key="profile" />, <DataExportSection key="export" />, <DeleteAccountSection key="delete" />]
          : tab === "security"
            ? [<PasswordSection key="password" />, <MfaSection key="mfa" />, <ConnectedAccountsSection key="connected" />, <SessionsSection key="sessions" />]
            : tab === "ai"
              ? [<PersonalModelSection key="model" eligible={personalModelOrgs ?? []} />, <PersonalAiSection key="stt" eligible={personalModelOrgs ?? []} />]
              : tab === "notifications"
                ? [<NotificationPrefs key="notifications" />]
                : tab === "calendars"
                  ? orgs.map((o) => <CalendarSettingsSection key={o.id} org={o} showOrgName={orgs.length > 1} />)
                  : tab === "device"
                    ? [<MeetingOfferSection key="meetings" />, <HotkeySettings key="hotkeys" />, <AboutSection key="about" />]
                    : [<AccessTokensSection key="tokens" />]
        ).map((section) => (
          <Card as="section" className="account-section" key={section.key}>
            {section}
          </Card>
        ))}
      </div>
    </div>
  );

  // At desktop width Settings is a page of the app frame (sidebar, top bar), framed by the org
  // last used; the frame's sidebar and account menu carry My account and Sign out. On phones,
  // and before someone has an org to frame it, it stays its own screen with its own top bar.
  const frameOrg = mobile ? null : (lastActiveOrg(orgs) ?? orgs[0] ?? null);
  if (frameOrg) {
    return (
      <AppShell org={frameOrg} user={user} section="settings" canRecord={user.email_verified ?? false}>
        <div className="account-framed">{content}</div>
        <MyAccountDialog open={accountOpen} onClose={closeAccount} />
      </AppShell>
    );
  }

  return (
    <div className="account-shell">
      <div className="account-topbar">
        <Link to="/orgs" aria-label="Back" className="account-back">
          <Icon name="arrow-left" />
        </Link>
        <Link to="/orgs" className="account-home" aria-label="Grackle home">
          <Wordmark size={21} />
        </Link>
        <div className="spacer" />
        {mobile ? null : (
          <Button variant="ghost" size="sm" icon="user" onClick={() => setAccountOpen(true)}>
            My account
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onSignOut}>
          Sign out
        </Button>
      </div>

      <div className="account-body">{content}</div>
      <MyAccountDialog open={accountOpen && !mobile} onClose={closeAccount} />
    </div>
  );
}
